import React, {
	forwardRef,
	memo,
	MutableRefObject,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";

import { createSlideShow, ISlideShow } from "../slideshow/slideshow-js";

import "../slideshow/slideshow.css";
import "./slideshow-preview.css";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

/** Use this type for typing the ref result */
export interface ISlidesShowPreviewRef {
	el: HTMLDivElement;
	previewEl: HTMLDivElement;
	slideShow: ISlideShow<ISpreadSlideShowItem>;
}

/**
 * A simple React hook for differentiating single and double clicks on the same component.
 *
 * @param {node} ref Dom node to watch for double clicks
 * @param {number} [latency=300] The amount of time (in milliseconds) to wait before differentiating a single from a double click
 * @param {function} onSingleClick A callback function for single click events
 * @param {function} onDoubleClick A callback function for double click events
 */
function useClickDoubleClick(
	ref: MutableRefObject<HTMLElement | null>,
	onSingleClick: (e: MouseEvent) => void = () => null,
	onDoubleClick: (e: MouseEvent) => void = () => null,
	latency = 300,
) {
	useEffect(() => {
		const clickRef = ref.current;
		let clickCount = 0;
		const handleClick = (e: MouseEvent) => {
			clickCount += 1;

			setTimeout(() => {
				if (clickCount === 1) onSingleClick(e);
				else if (clickCount === 2) onDoubleClick(e);

				clickCount = 0;
			}, latency);
		};

		// Add event listener for click events
		clickRef?.addEventListener("click", handleClick);

		// Remove event listener
		return () => {
			clickRef?.removeEventListener("click", handleClick);
		};
	});
}

export const SlideshowPreview = memo(
	forwardRef<
		ISlidesShowPreviewRef,
		{
			// Better to move onClick outside to useGesture?
			onClick?: React.DOMAttributes<HTMLDivElement>["onClick"];
			onMouseMove?: React.DOMAttributes<HTMLDivElement>["onMouseMove"];
			onDoubleClick: (e: MouseEvent) => void;
		}
	>(({ onClick, onDoubleClick, onMouseMove }, ref) => {
		const slideShowElRef = useRef<HTMLDivElement>(null);
		const slideShowPreviewRef = useRef<HTMLDivElement>(null);

		useClickDoubleClick(
			slideShowPreviewRef,
			onClick ? (e) => onClick(e as unknown as React.MouseEvent<HTMLDivElement>) : undefined,
			onDoubleClick,
		);

		/** Expose SlideShow object via ref */
		useImperativeHandle(
			ref,
			() => {
				if (!slideShowElRef.current || !slideShowPreviewRef.current) {
					throw new Error("useImperativeHandle: No SlideShowPreview element");
				}

				const slideShow = createSlideShow(
					slideShowElRef.current,
				) as ISlideShow<ISpreadSlideShowItem>;

				// Make slideshow available on console in 'Debug' mode
				// Assuming only one slideshow item is used, else global variable overridden
				if (eXpress.isDebugMode) {
					(window as any)._slideShow = slideShow;
				}

				return {
					el: slideShowElRef.current,
					previewEl: slideShowPreviewRef.current,
					slideShow,
				};
			},
			[],
		);

		return (
			<div
				className="slideshow-preview"
				role="img"
				ref={slideShowPreviewRef}
				onMouseMove={onMouseMove}
			>
				{/* Parent fullscreen div needed for drag handles */}
				<div ref={slideShowElRef} />
			</div>
		);
	}),
);
SlideshowPreview.displayName = "SlideshowPreview";
